import React, { useEffect, useState, useContext } from 'react';
import moment from 'moment';
import { getMonthlyProfitLoss, getTrialBalanceData } from '../../../Utility/Api';
import { NaviGate, shop_id } from '../../../Utility/function';
import { WarehouseContext } from '../../../Utility/UserContext';
import * as XLSX from 'xlsx';
import { 
  TrendingUp, 
  TrendingDown, 
  Calendar, 
  Package, 
  Landmark, 
  Factory, 
  Receipt, 
  ShoppingCart, 
  Printer, 
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  SlidersHorizontal,
  CheckCircle2,
  AlertCircle,
  BadgeDollarSign
} from 'lucide-react';

const CustomDateInput = ({ id, name, value, onChange, min, max, disabled, className, placeholder = 'DD/MM/YYYY' }) => {
  return (
    <div className="position-relative w-100">
      <input
        type="date"
        id={id}
        name={name}
        value={value || ''}
        min={min}
        max={max}
        disabled={disabled}
        onChange={onChange}
        onClick={(e) => {
          try {
            if (e.target.showPicker) {
              e.target.showPicker();
            }
          } catch (err) {}
        }}
        style={{
          opacity: 0,
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          cursor: disabled ? 'not-allowed' : 'pointer',
          zIndex: 10
        }}
      />
      <div 
        className={`form-control form-control-sm d-flex align-items-center justify-content-between ${className || ''}`} 
        style={{ cursor: disabled ? 'not-allowed' : 'pointer', minHeight: '31px', backgroundColor: '#fff' }}
      >
        <span className={value ? "text-dark fw-medium" : "text-muted"}>
          {value ? (moment(value).isValid() ? moment(value).format('DD/MM/YYYY') : value) : placeholder}
        </span>
        <Calendar size={14} className="text-secondary ms-1 shrink-0" />
      </div>
    </div>
  );
};

const MonthlyProfitLossReport = () => {
  const { showLoader, hideLoader, shopItem, subCompany } = useContext(WarehouseContext);
  const company = subCompany?.title || JSON.parse(sessionStorage.getItem('SUB-COMPANY'))?.title || JSON.parse(localStorage.getItem('SUB-COMPANY'))?.title || 'Company';
  
  // Date helpers
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  
  const formatDateStr = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateDisplay = (dateVal) => {
    if (!dateVal) return '-';
    const m = moment(dateVal);
    return m.isValid() ? m.format('DD/MM/YYYY') : dateVal;
  };

  const [fromDate, setFromDate] = useState(() => {
    return sessionStorage.getItem('MONTHLY_PL_FROM_DATE') || localStorage.getItem('MONTHLY_PL_FROM_DATE') || formatDateStr(firstDayOfMonth);
  });
  const [toDate, setToDate] = useState(() => {
    return sessionStorage.getItem('MONTHLY_PL_TO_DATE') || localStorage.getItem('MONTHLY_PL_TO_DATE') || formatDateStr(today);
  });
  const [loading, setLoading] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [monthlyList, setMonthlyList] = useState([]);
  const [trialData, setTrialData] = useState([]);
  const [activePreset, setActivePreset] = useState(() => {
    return sessionStorage.getItem('MONTHLY_PL_PRESET') || localStorage.getItem('MONTHLY_PL_PRESET') || 'thisMonth';
  });

  useEffect(() => {
    fetchReportData(fromDate, toDate);
  }, [shopItem, subCompany]);

  const fetchReportData = async (start, end) => {
    setLoading(true);
    if (showLoader) showLoader();
    try {
      const params = {};
      if (start) params.start_date = start;
      if (end) params.end_date = end;
      const currentShopId = shopItem?.shop_id || shop_id || JSON.parse(sessionStorage.getItem('SHOP'))?.shop_id;
      if (currentShopId) params.shop_id = currentShopId;

      const [res, trialRes] = await Promise.all([
        getMonthlyProfitLoss(params),
        getTrialBalanceData(params)
      ]);

      if (res.data && res.data.status === 'success') {
        setSummaryData(res.data.summary);
        setMonthlyList(res.data.monthly_breakdown || []);
      }
      if (trialRes && trialRes.data && trialRes.data.grouped_data) {
        setTrialData(trialRes.data.grouped_data || []);
      }
    } catch (error) {
      console.error('Error fetching monthly profit loss report:', error);
    } finally {
      setLoading(false);
      if (hideLoader) hideLoader();
    }
  };

  const handleFilter = (e) => {
    if (e) e.preventDefault();
    sessionStorage.setItem('MONTHLY_PL_FROM_DATE', fromDate);
    sessionStorage.setItem('MONTHLY_PL_TO_DATE', toDate);
    sessionStorage.setItem('MONTHLY_PL_PRESET', activePreset);
    fetchReportData(fromDate, toDate);
  };

  const setPresetRange = (preset) => {
    setActivePreset(preset);
    sessionStorage.setItem('MONTHLY_PL_PRESET', preset);
    const now = new Date();
    let start, end;

    if (preset === 'thisMonth') {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = now;
    } else if (preset === 'lastMonth') {
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = new Date(now.getFullYear(), now.getMonth(), 0);
    } else if (preset === 'last3Months') {
      start = new Date(now.getFullYear(), now.getMonth() - 2, 1);
      end = now;
    } else if (preset === 'thisYear') {
      start = new Date(now.getFullYear(), 0, 1);
      end = now;
    }

    if (start && end) {
      const sStr = formatDateStr(start);
      const eStr = formatDateStr(end);
      setFromDate(sStr);
      setToDate(eStr);
      sessionStorage.setItem('MONTHLY_PL_FROM_DATE', sStr);
      sessionStorage.setItem('MONTHLY_PL_TO_DATE', eStr);
      fetchReportData(sStr, eStr);
    }
  };

  const formatCurrency = (val) => {
    const num = Number(val || 0);
    return '₹ ' + num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const netProfitLoss = summaryData?.net_profit_loss ?? 0;
  const isProfit = summaryData?.is_profit ?? (netProfitLoss >= 0);
  const netAmount = netProfitLoss;
  const profitAmount = netProfitLoss > 0 ? netProfitLoss : 0;
  const lossAmount = netProfitLoss < 0 ? Math.abs(netProfitLoss) : 0;

  // Process Trial Balance data for Statement Breakdown (exact Chart of Accounts order, excluding totals)
  const orderedTypes = ["1", "2", "3", "3.2", "3.1", "4", "5", "6", "6.1", "6.2", "6.3", "7", "8", "9", "10", "12", "13", "14", "11", "15", "16", "17"];

  const sortedTrialData = orderedTypes
    .map((type) => trialData.find((item) => item.type === type))
    .filter(Boolean);

  const statementRows = sortedTrialData.map((item, index) => {
    // 1. Temporarily hide "Equity Balance" row (matching TrialBalance.js)
    if (item.remarks === "Equity Balance") {
      return null;
    }

    // 2. Force Offers/Discounts to display on Debit instead of Credit (matching TrialBalance.js)
    let isDebit = item.is_debit_display;
    if (item.remarks && item.remarks.toLowerCase().includes("offer")) {
      isDebit = true;
    }

    const debit = isDebit ? item.total_amount : 0;
    const credit = !isDebit ? item.total_amount : 0;
    const isProcessingCharges = item.type === "6.3";

    if (!item.total_amount && !isProcessingCharges) {
      return null;
    }

    return {
      index,
      type: item.type,
      accountName: item.remarks === "Loans" ? "Loans (Payable)" : item.remarks,
      debit: (debit && !isProcessingCharges) ? debit : 0,
      credit: (credit && !isProcessingCharges) ? credit : 0,
    };
  }).filter(Boolean);

  const loanItem = trialData.find(item => item.type === "14" || item.remarks === "Loans");
  const totalLoanAmount = loanItem ? (loanItem.total_amount || 0) : (summaryData?.total_loans || 0);

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    // Sheet 1: Monthly Breakdown
    const monthlyExportData = monthlyList.map(item => {
      const rowIsProfit = item.is_profit ?? (item.net_profit_loss >= 0);
      return {
        "Period / Month": item.month_label,
        "Opening Stock (INR)": parseFloat(item.opening_stock || 0).toFixed(2),
        "Opening Balance (INR)": parseFloat(item.opening_balance || 0).toFixed(2),
        "Production Cost (INR)": parseFloat(item.total_production_cost || 0).toFixed(2),
        "Total Expense (INR)": parseFloat(item.total_expense || 0).toFixed(2),
        "Total Sales (INR)": parseFloat(item.total_sales || 0).toFixed(2),
        "Closing Stock (INR)": parseFloat(item.closing_stock || 0).toFixed(2),
        "Closing Balance (INR)": parseFloat(item.closing_balance || 0).toFixed(2),
        "Net Profit/Loss (INR)": parseFloat(item.net_profit_loss || 0).toFixed(2),
        "Status": rowIsProfit ? "Profit" : "Loss"
      };
    });

    if (summaryData) {
      monthlyExportData.push({
        "Period / Month": "TOTAL SUMMARY",
        "Opening Stock (INR)": parseFloat(summaryData.opening_stock || 0).toFixed(2),
        "Opening Balance (INR)": parseFloat(summaryData.opening_balance || 0).toFixed(2),
        "Production Cost (INR)": parseFloat(summaryData.total_production_cost || 0).toFixed(2),
        "Total Expense (INR)": parseFloat(summaryData.total_expense || 0).toFixed(2),
        "Total Sales (INR)": parseFloat(summaryData.total_sales || 0).toFixed(2),
        "Closing Stock (INR)": parseFloat(summaryData.closing_stock || 0).toFixed(2),
        "Closing Balance (INR)": parseFloat(summaryData.closing_balance || 0).toFixed(2),
        "Net Profit/Loss (INR)": parseFloat(netAmount || 0).toFixed(2),
        "Status": summaryData.status || (isProfit ? "Profit" : "Loss")
      });
    }

    const ws1 = XLSX.utils.json_to_sheet(monthlyExportData);
    XLSX.utils.book_append_sheet(wb, ws1, "Monthly_Breakdown");

    // Sheet 2: Statement Breakdown
    if (statementRows && statementRows.length > 0) {
      const statementExportData = statementRows.map(row => ({
        "Account": row.accountName,
        "Debit (INR)": row.debit ? parseFloat(row.debit).toFixed(2) : "0.00",
        "Credit (INR)": row.credit ? parseFloat(row.credit).toFixed(2) : "0.00"
      }));
      const ws2 = XLSX.utils.json_to_sheet(statementExportData);
      XLSX.utils.book_append_sheet(wb, ws2, "Statement_Breakdown");
    }

    XLSX.writeFile(wb, `Monthly_Profit_Loss_${fromDate || 'start'}_to_${toDate || 'end'}.xlsx`);
  };

  const handlePrint = () => {
    // Remove any existing print frame
    const existingFrame = document.getElementById('print-iframe-helper');
    if (existingFrame) {
      existingFrame.remove();
    }

    const printFrame = document.createElement('iframe');
    printFrame.id = 'print-iframe-helper';
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = 'none';
    document.body.appendChild(printFrame);

    const printHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${company} - Monthly Profit & Loss Report</title>
        <style>
          @page {
            size: A4 portrait;
            margin: 10mm 12mm;
          }
          * {
            box-sizing: border-box;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
            color: #0f172a;
            background: #ffffff;
            margin: 0;
            padding: 0;
            font-size: 10pt;
            line-height: 1.35;
          }
          .print-header {
            text-align: center;
            border-bottom: 2px solid #0f172a;
            padding-bottom: 10px;
            margin-bottom: 14px;
          }
          .company-title {
            font-size: 18pt;
            font-weight: 800;
            margin: 0 0 3px 0;
            color: #0f172a;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .report-title {
            font-size: 12pt;
            font-weight: 700;
            margin: 0 0 3px 0;
            color: #334155;
          }
          .report-meta {
            font-size: 9pt;
            color: #64748b;
          }
          
          /* KPI Cards */
          .kpi-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 8px;
            margin-bottom: 12px;
          }
          .kpi-grid-3 {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 8px;
            margin-bottom: 14px;
          }
          .kpi-card {
            border: 1px solid #cbd5e1;
            border-radius: 6px;
            padding: 8px 10px;
            background: #f8fafc;
          }
          .kpi-title {
            font-size: 7.5pt;
            font-weight: 700;
            text-transform: uppercase;
            color: #64748b;
            margin-bottom: 3px;
          }
          .kpi-value {
            font-size: 11.5pt;
            font-weight: 800;
            color: #0f172a;
          }
          
          /* Tables */
          .section-title {
            font-size: 10pt;
            font-weight: 700;
            color: #0f172a;
            margin: 12px 0 6px 0;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            border-left: 3px solid #2563eb;
            padding-left: 6px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 12px;
            font-size: 8.5pt;
          }
          th, td {
            border: 1px solid #cbd5e1;
            padding: 5px 6px;
            text-align: right;
          }
          th {
            background-color: #f1f5f9 !important;
            font-weight: 700;
            color: #1e293b;
          }
          th:first-child, td:first-child {
            text-align: left;
          }
          .table-total {
            background-color: #f8fafc !important;
            font-weight: 800;
          }
          
          /* Dual Profit & Loss Boxes */
          .dual-card-row {
            display: flex;
            gap: 10px;
            margin: 12px 0;
          }
          .profit-box {
            flex: 1;
            border: 2px solid #10b981;
            background: #ecfdf5 !important;
            border-radius: 8px;
            padding: 10px 12px;
          }
          .loss-box {
            flex: 1;
            border: 2px solid #ef4444;
            background: #fef2f2 !important;
            border-radius: 8px;
            padding: 10px 12px;
          }
          .box-label {
            font-size: 8.5pt;
            font-weight: 700;
            text-transform: uppercase;
          }
          .profit-box .box-label { color: #047857; }
          .loss-box .box-label { color: #b91c1c; }
          .box-value {
            font-size: 15pt;
            font-weight: 900;
            margin: 3px 0;
          }
          .profit-box .box-value { color: #065f46; }
          .loss-box .box-value { color: #991b1b; }
          .box-desc {
            font-size: 7.5pt;
            color: #64748b;
          }

          /* Formula Box */
          .formula-card {
            border: 1px solid #cbd5e1;
            border-radius: 6px;
            padding: 10px;
            background: #f8fafc !important;
            margin-top: 10px;
          }
          .formula-row {
            display: flex;
            justify-content: space-between;
            font-size: 8.5pt;
            margin-bottom: 3px;
          }
          .formula-row.total {
            border-top: 1px solid #94a3b8;
            padding-top: 5px;
            margin-top: 5px;
            font-weight: 800;
            font-size: 10pt;
          }

          .print-footer {
            margin-top: 16px;
            text-align: center;
            font-size: 7.5pt;
            color: #94a3b8;
            border-top: 1px solid #e2e8f0;
            padding-top: 6px;
          }
        </style>
      </head>
      <body>
        <div class="print-header">
          <div class="company-title">${company}</div>
          <div class="report-title">Monthly Profit & Loss Report</div>
          <div class="report-meta">
            Period: <strong>${formatDateDisplay(summaryData?.start_date || fromDate)}</strong> to <strong>${formatDateDisplay(summaryData?.end_date || toDate)}</strong> &nbsp;|&nbsp; 
            Generated on: ${moment().format('DD/MM/YYYY, hh:mm:ss A')}
          </div>
        </div>

        <!-- KPI Grid 1 -->
        <div class="kpi-grid">
          <div class="kpi-card">
            <div class="kpi-title">Opening Stock</div>
            <div class="kpi-value">${formatCurrency(summaryData?.opening_stock)}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">Opening Balance</div>
            <div class="kpi-value">${formatCurrency(summaryData?.opening_balance)}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">Closing Stock</div>
            <div class="kpi-value">${formatCurrency(summaryData?.closing_stock)}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">Closing Balance</div>
            <div class="kpi-value">${formatCurrency(summaryData?.closing_balance)}</div>
          </div>
        </div>

        <!-- KPI Grid 2 -->
        <div class="kpi-grid">
          <div class="kpi-card">
            <div class="kpi-title">Total Production Cost</div>
            <div class="kpi-value" style="color: #ea580c;">${formatCurrency(summaryData?.total_production_cost)}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">Total Expense</div>
            <div class="kpi-value" style="color: #dc2626;">${formatCurrency(summaryData?.total_expense)}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">Total Sales (Revenue)</div>
            <div class="kpi-value" style="color: #16a34a;">${formatCurrency(summaryData?.total_sales)}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">Loans (Payable)</div>
            <div class="kpi-value" style="color: #0284c7;">${formatCurrency(totalLoanAmount)}</div>
          </div>
        </div>

        <!-- Monthly Overview Table -->
        <div class="section-title">Monthly Breakdown</div>
        <table>
          <thead>
            <tr>
              <th>Month / Period</th>
              <th>Opening Stock</th>
              <th>Opening Balance</th>
              <th>Production Cost</th>
              <th>Total Expense</th>
              <th>Total Sales</th>
              <th>Closing Stock</th>
              <th>Closing Balance</th>
              <th>Net Result</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${
              monthlyList && monthlyList.length > 0
                ? monthlyList.map(item => `
                  <tr>
                    <td>${item.month_label}</td>
                    <td>${formatCurrency(item.opening_stock)}</td>
                    <td>${formatCurrency(item.opening_balance)}</td>
                    <td>${formatCurrency(item.total_production_cost)}</td>
                    <td>${formatCurrency(item.total_expense)}</td>
                    <td style="font-weight: 700; color: #16a34a;">${formatCurrency(item.total_sales)}</td>
                    <td>${formatCurrency(item.closing_stock)}</td>
                    <td>${formatCurrency(item.closing_balance)}</td>
                    <td style="font-weight: 700; color: ${item.is_profit ? '#16a34a' : '#dc2626'};">
                      ${item.is_profit ? '+' : '-'} ${formatCurrency(Math.abs(item.net_profit_loss))}
                    </td>
                    <td>${item.status}</td>
                  </tr>
                `).join('')
                : '<tr><td colspan="10" style="text-align: center;">No records found</td></tr>'
            }
          </tbody>
          <tfoot>
            <tr class="table-total">
              <td>Total Summary</td>
              <td>${formatCurrency(summaryData?.opening_stock)}</td>
              <td>${formatCurrency(summaryData?.opening_balance)}</td>
              <td>${formatCurrency(summaryData?.total_production_cost)}</td>
              <td>${formatCurrency(summaryData?.total_expense)}</td>
              <td style="color: #16a34a;">${formatCurrency(summaryData?.total_sales)}</td>
              <td>${formatCurrency(summaryData?.closing_stock)}</td>
              <td>${formatCurrency(summaryData?.closing_balance)}</td>
              <td style="color: ${isProfit ? '#16a34a' : '#dc2626'};">
                ${isProfit ? '+' : '-'} ${formatCurrency(Math.abs(netProfitLoss))}
              </td>
              <td>${summaryData?.status || (isProfit ? 'Profit' : 'Loss')}</td>
            </tr>
          </tfoot>
        </table>

        <!-- Statement Breakdown -->
        <div class="section-title">Statement Breakdown</div>
        <table>
          <thead>
            <tr>
              <th style="text-align: left; padding-left: 10px;">Account</th>
              <th style="text-align: right; padding-right: 10px;">Debit (INR)</th>
              <th style="text-align: right; padding-right: 10px;">Credit (INR)</th>
            </tr>
          </thead>
          <tbody>
            ${
              statementRows && statementRows.length > 0
                ? statementRows.map(row => `
                  <tr>
                    <td style="text-align: left; padding-left: 10px;">${row.accountName}</td>
                    <td style="text-align: right; padding-right: 10px; color: #16a34a;">${row.debit ? formatCurrency(row.debit) : '-'}</td>
                    <td style="text-align: right; padding-right: 10px; color: #dc2626;">${row.credit ? formatCurrency(row.credit) : '-'}</td>
                  </tr>
                `).join('')
                : '<tr><td colspan="3" style="text-align: center;">No statement records found</td></tr>'
            }
          </tbody>
        </table>

        <!-- Dual Cards: Profit & Loss -->
        <div class="dual-card-row">
          <div class="profit-box">
            <div class="box-label">↑ Net Profit (Margin: ${summaryData?.profit_margin || 0}%)</div>
            <div class="box-value">${profitAmount > 0 ? '+ ' : ''}${formatCurrency(profitAmount)}</div>
            <div class="box-desc">${profitAmount > 0 ? 'Total net profit generated for the period' : 'Net profit is ₹ 0.00 for the period'}</div>
          </div>
          <div class="loss-box">
            <div class="box-label">↓ Net Loss</div>
            <div class="box-value">${lossAmount > 0 ? '- ' : ''}${formatCurrency(lossAmount)}</div>
            <div class="box-desc">${lossAmount > 0 ? 'Total net loss incurred for the period' : 'Zero loss incurred for the period (₹ 0.00)'}</div>
          </div>
        </div>

        <!-- Formula Box -->
        <div class="formula-card">
          <div style="font-weight: 700; font-size: 8.5pt; margin-bottom: 5px; text-transform: uppercase; color: #475569;">
            Calculation Formula
          </div>
          <div class="formula-row">
            <span>Total Sales (Revenue):</span>
            <span style="font-weight: 700;">${formatCurrency(summaryData?.total_sales)}</span>
          </div>
          <div class="formula-row">
            <span>(-) Total Production Cost:</span>
            <span style="font-weight: 700;">- ${formatCurrency(summaryData?.total_production_cost)}</span>
          </div>
          <div class="formula-row">
            <span>(-) Total Operating Expenses:</span>
            <span style="font-weight: 700;">- ${formatCurrency(summaryData?.total_expense)}</span>
          </div>
          <div class="formula-row total">
            <span>Final Net Result:</span>
            <span style="color: ${isProfit ? '#16a34a' : '#dc2626'};">
              ${isProfit ? `+ ${formatCurrency(profitAmount)} (Profit)` : `- ${formatCurrency(lossAmount)} (Loss)`}
            </span>
          </div>
        </div>

        <div class="print-footer">
          ${company} ERP System • Automated Financial Statement Report • Generated on ${new Date().toLocaleDateString()}
        </div>
      </body>
      </html>
    `;

    const printDoc = printFrame.contentDocument || printFrame.contentWindow.document;
    printDoc.open();
    printDoc.write(printHtml);
    printDoc.close();

    setTimeout(() => {
      if (printFrame.contentWindow) {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
      }
      setTimeout(() => {
        const frameToRemove = document.getElementById('print-iframe-helper');
        if (frameToRemove) {
          frameToRemove.remove();
        }
      }, 1500);
    }, 400);
  };

  return (
    <div className="page-wrappers">
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .sidebar-wrapper, .header-wrapper, nav, .btn, form, .no-print {
            display: none !important;
          }
          .page-wrappers, .page-content, .page-content * {
            visibility: visible;
          }
          .page-wrappers {
            position: absolute;
            left: 0;
            top: 0;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
        }
      `}</style>
      <div className="page-content py-3">
        <NaviGate />

        {/* Header and Controls */}
        <div className="card shadow-sm border-0 mb-4 bg-white rounded-3">
          <div className="card-body p-4">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 pb-3 border-bottom">
              <div>
                <span className="badge bg-primary-subtle text-primary fw-semibold px-3 py-1 mb-2 rounded-pill">
                  Financial Intelligence Report
                </span>
                <h4 className="fw-bold mb-1 text-dark">
                  {company} - Monthly Profit & Loss Report
                </h4>
                <p className="text-muted small mb-0">
                  Comprehensive performance analysis including stock valuations, operations, and net profitability.
                </p>
              </div>

              <div className="d-flex gap-2 align-items-center mt-3 mt-md-0">
                <button
                  type="button"
                  onClick={exportToExcel}
                  className="btn btn-success btn-sm px-3 shadow-sm"
                  title="Export to Excel"
                >
                  Export Excel
                </button>
                <button
                  type="button"
                  onClick={handlePrint}
                  className="btn btn-outline-secondary btn-sm px-3 d-flex align-items-center gap-2 shadow-sm"
                >
                  <Printer size={16} /> Print Report
                </button>
                <button
                  type="button"
                  onClick={() => fetchReportData(fromDate, toDate)}
                  className="btn btn-primary btn-sm px-3 d-flex align-items-center gap-2 shadow-sm"
                  disabled={loading}
                >
                  <RefreshCw size={16} className={loading ? 'spinner-border spinner-border-sm' : ''} /> 
                  Refresh
                </button>
              </div>
            </div>

            {/* Presets and Date Filter Form */}
            <form onSubmit={handleFilter} className="row g-3 align-items-end">
              <div className="col-12 col-xl-5">
                <label className="form-label small fw-semibold text-muted mb-2 d-flex align-items-center gap-1">
                  <SlidersHorizontal size={14} /> Quick Presets
                </label>
                <div className="btn-group w-100 shadow-sm" role="group">
                  <button
                    type="button"
                    className={`btn btn-sm ${activePreset === 'thisMonth' ? 'btn-dark' : 'btn-outline-secondary'}`}
                    onClick={() => setPresetRange('thisMonth')}
                  >
                    This Month
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${activePreset === 'lastMonth' ? 'btn-dark' : 'btn-outline-secondary'}`}
                    onClick={() => setPresetRange('lastMonth')}
                  >
                    Last Month
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${activePreset === 'last3Months' ? 'btn-dark' : 'btn-outline-secondary'}`}
                    onClick={() => setPresetRange('last3Months')}
                  >
                    Last 3 Months
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${activePreset === 'thisYear' ? 'btn-dark' : 'btn-outline-secondary'}`}
                    onClick={() => setPresetRange('thisYear')}
                  >
                    This Year
                  </button>
                </div>
              </div>

              <div className="col-6 col-md-3 col-xl-3">
                <label htmlFor="fromDate" className="form-label small fw-semibold text-muted mb-1 d-flex align-items-center gap-1">
                  <Calendar size={14} /> From Date
                </label>
                <CustomDateInput
                  id="fromDate"
                  name="fromDate"
                  value={fromDate}
                  max={toDate}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFromDate(val);
                    setActivePreset('custom');
                    sessionStorage.setItem('MONTHLY_PL_FROM_DATE', val);
                    sessionStorage.setItem('MONTHLY_PL_PRESET', 'custom');
                  }}
                />
              </div>

              <div className="col-6 col-md-3 col-xl-3">
                <label htmlFor="toDate" className="form-label small fw-semibold text-muted mb-1 d-flex align-items-center gap-1">
                  <Calendar size={14} /> To Date
                </label>
                <CustomDateInput
                  id="toDate"
                  name="toDate"
                  value={toDate}
                  min={fromDate}
                  onChange={(e) => {
                    const val = e.target.value;
                    setToDate(val);
                    setActivePreset('custom');
                    sessionStorage.setItem('MONTHLY_PL_TO_DATE', val);
                    sessionStorage.setItem('MONTHLY_PL_PRESET', 'custom');
                  }}
                />
              </div>

              <div className="col-12 col-md-2 col-xl-1">
                <button
                  type="submit"
                  className="btn btn-success btn-sm w-100 fw-semibold d-flex align-items-center justify-content-center gap-1"
                  disabled={loading}
                >
                  Filter
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Main KPI Summary Cards Grid */}
        <div className="row g-3 mb-4">
          {/* Opening Stock */}
          <div className="col-12 col-sm-6 col-lg-3">
            <div className="card border-0 shadow-sm h-100 rounded-3" style={{ borderLeft: '4px solid #3b82f6' }}>
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted small fw-semibold text-uppercase">Opening Stock</span>
                  <div className="p-2 rounded-circle bg-primary bg-opacity-10 text-primary">
                    <Package size={20} />
                  </div>
                </div>
                <h4 className="fw-bold text-dark mb-1">
                  {formatCurrency(summaryData?.opening_stock)}
                </h4>
                <div className="small text-muted">Stock value before {formatDateDisplay(summaryData?.start_date || fromDate)}</div>
              </div>
            </div>
          </div>

          {/* Opening Balance */}
          <div className="col-12 col-sm-6 col-lg-3">
            <div className="card border-0 shadow-sm h-100 rounded-3" style={{ borderLeft: '4px solid #6366f1' }}>
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted small fw-semibold text-uppercase">Opening Balance</span>
                  <div className="p-2 rounded-circle bg-indigo bg-opacity-10 text-indigo" style={{ backgroundColor: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' }}>
                    <Landmark size={20} />
                  </div>
                </div>
                <h4 className="fw-bold text-dark mb-1">
                  {formatCurrency(summaryData?.opening_balance)}
                </h4>
                <div className="small text-muted">Bank/Cash before {formatDateDisplay(summaryData?.start_date || fromDate)}</div>
              </div>
            </div>
          </div>

          {/* Closing Stock */}
          <div className="col-12 col-sm-6 col-lg-3">
            <div className="card border-0 shadow-sm h-100 rounded-3" style={{ borderLeft: '4px solid #06b6d4' }}>
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted small fw-semibold text-uppercase">Closing Stock</span>
                  <div className="p-2 rounded-circle bg-info bg-opacity-10 text-info">
                    <Package size={20} />
                  </div>
                </div>
                <h4 className="fw-bold text-dark mb-1">
                  {formatCurrency(summaryData?.closing_stock)}
                </h4>
                <div className="small text-muted">Stock valuation as on {formatDateDisplay(summaryData?.end_date || toDate)}</div>
              </div>
            </div>
          </div>

          {/* Closing Balance */}
          <div className="col-12 col-sm-6 col-lg-3">
            <div className="card border-0 shadow-sm h-100 rounded-3" style={{ borderLeft: '4px solid #8b5cf6' }}>
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted small fw-semibold text-uppercase">Closing Balance</span>
                  <div className="p-2 rounded-circle bg-purple bg-opacity-10 text-purple" style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>
                    <Landmark size={20} />
                  </div>
                </div>
                <h4 className="fw-bold text-dark mb-1">
                  {formatCurrency(summaryData?.closing_balance)}
                </h4>
                <div className="small text-muted">Bank/Cash as on {formatDateDisplay(summaryData?.end_date || toDate)}</div>
              </div>
            </div>
          </div>

          {/* Total Production Cost */}
          {/* Total Production Cost */}
          <div className="col-12 col-sm-6 col-lg-3">
            <div className="card border-0 shadow-sm h-100 rounded-3" style={{ borderLeft: '4px solid #f97316' }}>
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted small fw-semibold text-uppercase">Total Production Cost</span>
                  <div className="p-2 rounded-circle bg-warning bg-opacity-10 text-warning">
                    <Factory size={20} />
                  </div>
                </div>
                <h4 className="fw-bold text-dark mb-1">
                  {formatCurrency(summaryData?.total_production_cost)}
                </h4>
                <div className="small text-muted d-flex justify-content-between">
                  <span>COGS: {formatCurrency(summaryData?.breakdown?.cogs)}</span>
                  <span>Processing: {formatCurrency(summaryData?.breakdown?.processing_charges)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Total Expense */}
          <div className="col-12 col-sm-6 col-lg-3">
            <div className="card border-0 shadow-sm h-100 rounded-3" style={{ borderLeft: '4px solid #ef4444' }}>
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted small fw-semibold text-uppercase">Total Expense</span>
                  <div className="p-2 rounded-circle bg-danger bg-opacity-10 text-danger">
                    <Receipt size={20} />
                  </div>
                </div>
                <h4 className="fw-bold text-dark mb-1">
                  {formatCurrency(summaryData?.total_expense)}
                </h4>
                <div className="small text-muted d-flex justify-content-between">
                  <span>General: {formatCurrency(summaryData?.breakdown?.operating_expense)}</span>
                  <span>Salaries: {formatCurrency(summaryData?.breakdown?.salary_expense)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Total Sales */}
          <div className="col-12 col-sm-6 col-lg-3">
            <div className="card border-0 shadow-sm h-100 rounded-3" style={{ borderLeft: '4px solid #10b981' }}>
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted small fw-semibold text-uppercase">Total Sales</span>
                  <div className="p-2 rounded-circle bg-success bg-opacity-10 text-success">
                    <ShoppingCart size={20} />
                  </div>
                </div>
                <h4 className="fw-bold text-success mb-1">
                  {formatCurrency(summaryData?.total_sales)}
                </h4>
                <div className="small text-muted">
                  Sales Revenue: {formatCurrency(summaryData?.breakdown?.sales_revenue)}
                </div>
              </div>
            </div>
          </div>

          {/* Loans (Payable) */}
          <div className="col-12 col-sm-6 col-lg-3">
            <div className="card border-0 shadow-sm h-100 rounded-3" style={{ borderLeft: '4px solid #0284c7' }}>
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted small fw-semibold text-uppercase">Loans (Payable)</span>
                  <div className="p-2 rounded-circle bg-info bg-opacity-10 text-info">
                    <BadgeDollarSign size={20} />
                  </div>
                </div>
                <h4 className="fw-bold text-dark mb-1">
                  {formatCurrency(totalLoanAmount)}
                </h4>
                <div className="small text-muted">
                  Outstanding loan liability for the period
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Monthly Breakdown Table */}
        <div className="card shadow-sm border-0 mb-4 rounded-3">
          <div className="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center">
            <h6 className="fw-bold mb-0 text-dark">
              Monthly Financial Overview
            </h6>
            <span className="text-muted small">
              Period: {formatDateDisplay(summaryData?.start_date || fromDate)} to {formatDateDisplay(summaryData?.end_date || toDate)}
            </span>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0 text-center">
                <thead className="table-dark text-white">
                  <tr>
                    <th className="text-start ps-3">Period / Month</th>
                    <th>Opening Stock</th>
                    <th>Opening Balance</th>
                    <th>Production Cost</th>
                    <th>Total Expense</th>
                    <th>Total Sales</th>
                    <th>Closing Stock</th>
                    <th>Closing Balance</th>
                    <th>Net Profit/Loss</th>
                    <th className="pe-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyList.length > 0 ? (
                    monthlyList.map((item, idx) => {
                      const rowIsProfit = item.is_profit ?? (item.net_profit_loss >= 0);
                      return (
                        <tr key={idx}>
                          <td className="text-start ps-3 fw-semibold text-dark">
                            {item.month_label}
                          </td>
                          <td className="text-muted">{formatCurrency(item.opening_stock)}</td>
                          <td className="text-muted">{formatCurrency(item.opening_balance)}</td>
                          <td className="text-warning fw-semibold">{formatCurrency(item.total_production_cost)}</td>
                          <td className="text-danger fw-semibold">{formatCurrency(item.total_expense)}</td>
                          <td className="text-success fw-bold">{formatCurrency(item.total_sales)}</td>
                          <td className="text-muted">{formatCurrency(item.closing_stock)}</td>
                          <td className="text-muted">{formatCurrency(item.closing_balance)}</td>
                          <td className={`fw-bold ${rowIsProfit ? 'text-success' : 'text-danger'}`}>
                            {rowIsProfit ? '+' : '-'} {formatCurrency(Math.abs(item.net_profit_loss))}
                          </td>
                          <td className="pe-3">
                            <span className={`badge px-2 py-1 rounded-pill ${rowIsProfit ? 'bg-success' : 'bg-danger'}`}>
                              {rowIsProfit ? 'Profit' : 'Loss'}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center py-4 text-muted">
                        No financial records found for the selected date range.
                      </td>
                    </tr>
                  )}
                </tbody>
                {summaryData && (
                  <tfoot className="table-light fw-bold border-top border-2">
                    <tr>
                      <td className="text-start ps-3">Total Summary</td>
                      <td>{formatCurrency(summaryData.opening_stock)}</td>
                      <td>{formatCurrency(summaryData.opening_balance)}</td>
                      <td className="text-warning">{formatCurrency(summaryData.total_production_cost)}</td>
                      <td className="text-danger">{formatCurrency(summaryData.total_expense)}</td>
                      <td className="text-success">{formatCurrency(summaryData.total_sales)}</td>
                      <td>{formatCurrency(summaryData.closing_stock)}</td>
                      <td>{formatCurrency(summaryData.closing_balance)}</td>
                      <td className={isProfit ? 'text-success' : 'text-danger'}>
                        {isProfit ? '+' : '-'} {formatCurrency(Math.abs(netAmount))}
                      </td>
                      <td className="pe-3">
                        <span className={`badge px-2 py-1 rounded-pill ${isProfit ? 'bg-success' : 'bg-danger'}`}>
                          {summaryData.status}
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>

        {/* Statement Overview / Trial Balance Statement Breakdown */}
        <div className="card shadow-sm border-0 mb-4 rounded-3">
          <div className="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center">
            <h6 className="fw-bold mb-0 text-dark">Statement Breakdown</h6>
            <span className="text-muted small">
              Period: {formatDateDisplay(summaryData?.start_date || fromDate)} to {formatDateDisplay(summaryData?.end_date || toDate)}
            </span>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover table-striped table-bordered align-middle mb-0 text-center">
                <thead className="table-secondary">
                  <tr>
                    <th className="text-start ps-4" style={{ width: '40%' }}>Account</th>
                    <th className="text-end pe-4 text-success" style={{ width: '30%' }}>Debit (INR)</th>
                    <th className="text-end pe-4 text-danger" style={{ width: '30%' }}>Credit (INR)</th>
                  </tr>
                </thead>
                <tbody>
                  {statementRows.length > 0 ? (
                    statementRows.map((row, idx) => (
                      <tr key={idx}>
                        <td className="text-start ps-4 fw-medium text-dark">
                          {row.accountName}
                        </td>
                        <td className="text-end pe-4 text-success font-medium">
                          {row.debit ? formatCurrency(row.debit) : "-"}
                        </td>
                        <td className="text-end pe-4 text-danger font-medium">
                          {row.credit ? formatCurrency(row.credit) : "-"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="text-center py-4 text-muted">
                        No statement accounts recorded for the selected period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* 🌟 DUAL PROFIT & LOSS SUMMARY CARDS (SHOWS BOTH PROFIT ↑ & LOSS ↓) 🌟 */}
        <div className="row g-3 mb-4">
          {/* Profit Summary Card */}
          <div className="col-12 col-md-6">
            <div 
              className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden" 
              style={{
                background: 'linear-gradient(135deg, #065f46 0%, #047857 50%, #10b981 100%)',
                color: '#ffffff'
              }}
            >
              <div className="card-body p-4 d-flex align-items-center justify-content-between">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="badge bg-white bg-opacity-25 text-white px-3 py-1 rounded-pill small fw-semibold text-uppercase" style={{ letterSpacing: '0.5px' }}>
                      ↑ Net Profit
                    </span>
                    {isProfit && (
                      <span className="badge bg-white text-dark px-2 py-1 rounded-pill small fw-bold">
                        Margin: {summaryData?.profit_margin || 0}%
                      </span>
                    )}
                  </div>
                  <h2 className="fw-bold mb-1 text-white display-6">
                    {profitAmount > 0 ? '+ ' : ''}{formatCurrency(profitAmount)}
                  </h2>
                  <small className="text-white text-opacity-80">
                    {profitAmount > 0 ? 'Total net profit generated for this period' : 'Net profit is ₹ 0.00 for this period'}
                  </small>
                </div>
                <div 
                  className="d-flex align-items-center justify-content-center p-3 rounded-circle shadow-lg"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', width: '75px', height: '75px', flexShrink: 0 }}
                >
                  <TrendingUp size={44} strokeWidth={2.5} className="text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Loss Summary Card */}
          <div className="col-12 col-md-6">
            <div 
              className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden" 
              style={{
                background: 'linear-gradient(135deg, #881337 0%, #b91c1c 50%, #ef4444 100%)',
                color: '#ffffff'
              }}
            >
              <div className="card-body p-4 d-flex align-items-center justify-content-between">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="badge bg-white bg-opacity-25 text-white px-3 py-1 rounded-pill small fw-semibold text-uppercase" style={{ letterSpacing: '0.5px' }}>
                      ↓ Net Loss
                    </span>
                    {!isProfit && (
                      <span className="badge bg-white text-dark px-2 py-1 rounded-pill small fw-bold">
                        Margin: {summaryData?.profit_margin || 0}%
                      </span>
                    )}
                  </div>
                  <h2 className="fw-bold mb-1 text-white display-6">
                    {lossAmount > 0 ? '- ' : ''}{formatCurrency(lossAmount)}
                  </h2>
                  <small className="text-white text-opacity-80">
                    {lossAmount > 0 ? 'Total net loss incurred for this period' : 'Zero loss incurred for this period (₹ 0.00)'}
                  </small>
                </div>
                <div 
                  className="d-flex align-items-center justify-content-center p-3 rounded-circle shadow-lg"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', width: '75px', height: '75px', flexShrink: 0 }}
                >
                  <TrendingDown size={44} strokeWidth={2.5} className="text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 🌟 FOOTER SUMMARY & CALCULATION FORMULA CARD 🌟 */}
        <div 
          className="card border-0 shadow-lg rounded-4 overflow-hidden mb-4"
          style={{
            background: isProfit 
              ? 'linear-gradient(135deg, #064e3b 0%, #065f46 60%, #059669 100%)' 
              : 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 60%, #dc2626 100%)',
            color: '#ffffff'
          }}
        >
          <div className="card-body p-4 p-md-5">
            <div className="row align-items-center g-4">
              
              {/* Left Column: Summary & Status */}
              <div className="col-12 col-lg-7">
                <div className="d-flex align-items-center gap-3 mb-3">
                  <span className="badge bg-white bg-opacity-25 text-white px-3 py-1 rounded-pill fw-semibold text-uppercase" style={{ letterSpacing: '1px' }}>
                    Statement Outcome
                  </span>
                  <span className="badge bg-white text-dark px-3 py-1 rounded-pill fw-bold">
                    Net Result: {isProfit ? 'PROFIT' : 'LOSS'} ({isProfit ? `+ ${formatCurrency(profitAmount)}` : `- ${formatCurrency(lossAmount)}`})
                  </span>
                </div>

                <div className="row g-3 mb-3">
                  {/* Profit Sub-box */}
                  <div className="col-6">
                    <div 
                      className="p-3 rounded-3"
                      style={{ 
                        backgroundColor: isProfit ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.25)',
                        border: isProfit ? '2px solid rgba(255, 255, 255, 0.8)' : '1px solid rgba(255, 255, 255, 0.2)'
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <span className="small fw-semibold text-white text-opacity-90">↑ Profit Amount</span>
                        <div className="p-1 rounded bg-success text-white">
                          <TrendingUp size={16} />
                        </div>
                      </div>
                      <h4 className="fw-bold mb-0 text-white">
                        {profitAmount > 0 ? '+ ' : ''}{formatCurrency(profitAmount)}
                      </h4>
                    </div>
                  </div>

                  {/* Loss Sub-box */}
                  <div className="col-6">
                    <div 
                      className="p-3 rounded-3"
                      style={{ 
                        backgroundColor: !isProfit ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.25)',
                        border: !isProfit ? '2px solid rgba(255, 255, 255, 0.8)' : '1px solid rgba(255, 255, 255, 0.2)'
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <span className="small fw-semibold text-white text-opacity-90">↓ Loss Amount</span>
                        <div className="p-1 rounded bg-danger text-white">
                          <TrendingDown size={16} />
                        </div>
                      </div>
                      <h4 className="fw-bold mb-0 text-white">
                        {lossAmount > 0 ? '- ' : ''}{formatCurrency(lossAmount)}
                      </h4>
                    </div>
                  </div>
                </div>

                <p className="mb-0 text-white text-opacity-90 small">
                  {isProfit
                    ? `Summary: The business generated a net profit of ${formatCurrency(profitAmount)} with ₹ 0.00 loss for the selected period.`
                    : `Summary: The business incurred a net loss of ${formatCurrency(lossAmount)} with ₹ 0.00 profit for the selected period.`}
                </p>
              </div>

              {/* Right Column: Calculation Formula */}
              <div className="col-12 col-lg-5">
                <div 
                  className="p-4 rounded-3 h-100"
                  style={{ 
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    backdropFilter: 'blur(8px)'
                  }}
                >
                  <div className="small fw-bold text-white text-opacity-75 mb-3 text-uppercase">
                    Calculation Formula
                  </div>
                  <div className="d-flex justify-content-between small text-white text-opacity-90 mb-2">
                    <span>Total Sales (Revenue):</span>
                    <span className="fw-bold">{formatCurrency(summaryData?.total_sales)}</span>
                  </div>
                  <div className="d-flex justify-content-between small text-white text-opacity-90 mb-2">
                    <span>(-) Production Cost:</span>
                    <span className="fw-bold">- {formatCurrency(summaryData?.total_production_cost)}</span>
                  </div>
                  <div className="d-flex justify-content-between small text-white text-opacity-90 mb-3">
                    <span>(-) Total Expenses:</span>
                    <span className="fw-bold">- {formatCurrency(summaryData?.total_expense)}</span>
                  </div>
                  <div className="d-flex justify-content-between border-top border-white border-opacity-25 pt-2 fw-bold text-white fs-6">
                    <span>Net Result:</span>
                    <span className={isProfit ? 'text-white' : 'text-white'}>
                      {isProfit ? `+ ${formatCurrency(profitAmount)} (Profit)` : `- ${formatCurrency(lossAmount)} (Loss)`}
                    </span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default MonthlyProfitLossReport;
